import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useInternationalization } from '@progress/kendo-react-intl';
import { SWITCH_CASH_FLOW_MODAL } from '../../../../../redux/types';

import { INote, IWidgetCombinedData } from '../../../../../common/services/http/analysisContentService';
import { ISetup } from '../../../types/ISetup';
import { ISummaryDefinition } from '../../../types/ISummaryDefinition';
import { SummaryValueItem } from '../../../types/ISummaryValueItem';
import { ICurrency } from '../../../types/ICurrency';
import { IWidget } from '../../../../../common/services/http/types/IWidget';
import { AnalysisContentWidgetModel } from '../../../../../models/AnalysisContentWidgetModel';
import { IMeasure } from '../../../types/IMeasure';
import { IStaticProperty } from '../../../../../models/types/IStaticProperty';
import { AnalysisContentWidgetDataModel } from '../../../../../models/AnalysisContentWidgetDataModel';

const useGetSummaryData = (
  setup: ISetup,
  widget: AnalysisContentWidgetModel | IWidget,
  measures: IMeasure[],
  notes: INote[],
  boardId,
  staticProperties: IStaticProperty[],
  scenarios,
  selectedObject,
  selectedPeriod,
  boardSelectedCurrency,
  investorObjectId,
) => {
  const [summaryContent, setSummaryContent] = useState<ISummaryDefinition[]>([]);
  const [summaryContentEnriched, setSummaryContentEnriched] = useState<ISummaryDefinition[]>([]);
  const [values, setValues] = useState<SummaryValueItem[]>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency[]>([]);
  const [summaryNotes, setSummaryNotes] = useState<INote[]>([]);
  const [notesShown, setNotesShown] = useState<any>({ showNote: '' });
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [localCurr, setLocalCurr] = useState<string>('');

  const widgetDataRef = useRef(null);
  const intl = useInternationalization();
  const dispatch = useDispatch();

  const investor = useSelector((state: RootState) => state.investor.investor);
  const { investorId } = useParams();

  const filterNotesForSummary = (notes: INote[]): void => {
    let tempSummaryNotes = [];
    if (notes) {
      for (let i = 0; i < notes.length; i++) {
        for (let j = 0; j < notes[i].measures.length; j++) {
          let tempNoteMeasureId = notes[i].measures[j].measureId;
          summaryContent.map((el) => {
            if (el.measureId === tempNoteMeasureId && !tempSummaryNotes.includes(notes[i])) {
              tempSummaryNotes.push(notes[i]);
            }
          });
        }
      }
    }
    setSummaryNotes(tempSummaryNotes);
    if (notes) extractNotesForSummaryItems();
  };

  const extractNotesForSummaryItems = (): void => {
    let copiedSummaryContent = summaryContent.map((object) => ({ ...object }));
    for (let i = 0; i < summaryContent.length; i++) {
      let tempItemNotes = [];
      for (let j = 0; j < notes.length; j++) {
        let tempNoteMeasures = notes[j].measures.map((el) => el.measureId);
        if (tempNoteMeasures.includes(summaryContent[i].measureId)) {
          tempItemNotes.push(notes[j]);
          copiedSummaryContent[i].hasNotes = true;
          copiedSummaryContent[i].notes = tempItemNotes;
          setSummaryContentEnriched(copiedSummaryContent);
        }
      }
    }
  };

  const initializeItems = (data: IWidgetCombinedData): SummaryValueItem[] => {
    const values: SummaryValueItem[] = [];

    for (let i = 0; i < setup.definition.length; i++) {
      let def = setup.definition[i];
      if (def.measureId) {
        let measureId = def.measureId;
        let measure = _findMeasure(measureId);
        let val = data.measureValues.find((item) => item.measureId === measureId);
        let amount = val ? val.amount / def.amountsIn : 0;
        values.push({
          name: def.name,
          scale: def.scale,
          isPercent: measure && measure.isPercent,
          amount: amount,
          formatAmount: measure && intl.toString(amount, (measure.isPercent ? 'p' : 'n') + def.scale),
          measureId: measureId,
          measure: measure,
          templatePropertyId: null,
          viewCashflows: def.viewCashflows,
          valueId: val ? val.id : 0,
          scenarioId: val ? val.scenarioId : 0,
          nodeId: val ? val.nodeId : 0,
        });
      }
      if (def.templatePropertyId) {
        let staticProperty = _findProperty(def.templatePropertyId);
        let staticVal = data.staticValues.find(function(item) {
          return item.templatePropertyId === def.templatePropertyId;
        });
        values.push({
          name: def.name,
          scale: def.scale,
          isPercent: staticProperty && staticProperty.dataType === 'percent',
          amount: staticVal && staticVal && staticVal.rawValue,
          formatAmount: staticProperty && staticVal ? _formatStaticPropertyValue(staticProperty, staticVal.rawValue) : '',
          measureId: null,
          measure: null,
          templatePropertyId: def.templatePropertyId,
          viewCashflows: false,
          valueId: 0,
          scenarioId: 0,
          nodeId: staticVal ? staticVal.nodeId : 0,
        });
      }
    }

    return values;
  };

  const _formatStaticPropertyValue = (staticProperty: IStaticProperty, value: string): string => {
    if (!value) return '';

    if (staticProperty.dataType === 'number') return intl.toString(Number.parseFloat(value), `n${staticProperty.scale}`);

    if (staticProperty.dataType === 'date') return intl.toString(new Date(value), 'd');

    if (staticProperty.dataType === 'percent') return intl.toString(Number.parseFloat(value), `p${staticProperty.scale}`);

    return value;
  };

  const _findMeasure = (measureId: number): IMeasure => {
    if (measures) {
      return measures.find((item) => item.id === measureId);
    }
  };

  const _findProperty = (templatePropertyId: number) => {
    return staticProperties && staticProperties.find(function(item) {
      return item.id === templatePropertyId;
    });
  };

  const cashflowDialogInfo = (value: SummaryValueItem): any => {
    return {
      nodeId: value.nodeId,
      scenarioId: value.scenarioId,
      valueId: value.valueId,
    };
  };

  const handleSummaryValueClick = (value: SummaryValueItem): void => {
    let cashFlowInfo = cashflowDialogInfo(value);
    dispatch({
      type: SWITCH_CASH_FLOW_MODAL,
      payload: {
        dataItem: { ...value, currency: localCurr },
        cashFlowInfo: cashFlowInfo,
        investorObjectId: investorObjectId,
        value: value.formatAmount,
        boardId: boardId,
        selectedCurrency: selectedCurrency,
      },
    });
  };

  const toggleNotes = (id: string): void => {
    setNotesShown({ showNote: id });
  };

  const clickOutsideHandler = (): void => {
    setNotesShown({ showNote: '' });
  };

  const measuresForFetch = () => {
    let measuresForFetch = [];
    setup.definition.forEach(function(def) {
      if (
        def.measureId &&
        !measuresForFetch.find(function(item) {
          return item.measureId === def.measureId;
        })
      )
        measuresForFetch.push({ measureId: def.measureId });
    });
    return measuresForFetch;
  };

  const staticPropertiesForFetch = () => {
    let propertiesForFetch = [];
    setup.definition.forEach(function(def) {
      if (
        def.templatePropertyId &&
        !propertiesForFetch.find(function(item) {
          return item.templatePropertyId === def.templatePropertyId;
        })
      )
        propertiesForFetch.push({ templatePropertyId: def.templatePropertyId });
    });
    return propertiesForFetch;
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  const handleFindSelection = (currency, arrayToFind) => {
    if (arrayToFind.find((curr) => curr.currency === currency.currency)) {
      return currency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    widgetDataRef.current = new AnalysisContentWidgetDataModel(widget, measuresForFetch(), investorId ? parseInt(investorId) : parseInt(investorObjectId), false, staticPropertiesForFetch());
    widgetDataRef.current.defineNodes(selectedObject);
    setLocalCurr(widgetDataRef.current.nodes[0].currency);
  }, [investor]);

  useEffect(() => {
    filterNotesForSummary(notes);
  }, [notes]);

  useEffect(() => {
    if (boardSelectedCurrency) {
      let currency = handleFindSelection(boardSelectedCurrency, widgetDataRef.current.generateCurrencies(scenarios));
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [boardSelectedCurrency]);

  useEffect(() => {
    //@ts-ignore
    if (selectedCurrency.currency) {
      setSummaryContent(setup.definition);
      setSummaryContentEnriched(setup.definition);
      widgetDataRef.current.defineNodes(selectedObject);
      setCurrencies(widgetDataRef.current.generateCurrencies(scenarios));
      if (investorId) {
        widgetDataRef.current.setInvestorId(investorId);
      } else {
        widgetDataRef.current.setInvestorId(investorObjectId);
      }
      //@ts-ignore
      widgetDataRef.current.fetchCombinedData(selectedPeriod, selectedCurrency.currency).then((res: IWidgetCombinedData) => {
        const val = initializeItems(res);
        setValues(val);
        setLoading(false);
      });
    }
  }, [selectedObject, selectedPeriod,selectedCurrency]);

  return {
    summaryContentEnriched,
    values,
    loading,
    summaryNotes,
    notesShown,
    handleSummaryValueClick,
    toggleNotes,
    clickOutsideHandler,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
    localCurr,
  };
};

export default useGetSummaryData;
